import { Box } from '@chakra-ui/layout';
import { useState } from 'react';

import { useCurrencyPreference } from '../../../../../shared/user-preferences/hooks/useCurrencyPreference';
import { useEffectiveFilters } from '../../../filters/hooks/useEffectiveFilters';
import type { PrimaryFilterId } from '../../../filters/types/filters';
import { CompensationMapDataProvider } from '../../DataProvider/compensation/CompensationMapDataProvider';
import { CompositionMapDataProvider } from '../../DataProvider/composition/CompositionMapDataProvider';
import { PostingMapDataProvider } from '../../DataProvider/PostingMapDataProvider';
import { ScreenerMapDataProvider } from '../../DataProvider/ScreenerMapDataProvider';
import { SentimentMapDataProvider } from '../../DataProvider/SentimentMapDataProvider';
import { TalentDiscoveryDataProvider } from '../../DataProvider/TalentDiscoveryDataProvider';
import { TalentDiscoveryMapDataProvider } from '../../DataProvider/TalentDiscoveryMapDataProvider';
import { TransitionMapDataProvider } from '../../DataProvider/TransitionMapDataProvider';
import { useTabContentConfig } from '../../PageConfigProvider/PageConfigProvider';
import { RevelioPageId } from '../../PageConfigProvider/types';
import type {
  DataNormalization,
  DataTransformation,
  MapPlotOptions,
} from '../../PageConfigProvider/types/item';
import {
  isPayMetric,
  isPostingMetric,
  isScreenerMetric,
  isSentimentMetric,
  isTalentDiscoveryMetric,
  isTransitionMetric,
  type MetricValues,
} from '../../PageConfigProvider/types/metric';
import { ChartFallback } from '../ChartFallback';
import { ChartLoader } from '../ChartLoader';
import { ChartNotSupported } from '../ChartNotSupported';
import { GenericMap } from './GenericMap';
import type { MapPoint } from './types';

const MapWithLoader = ({
  points,
  metric,
  normalization,
  transformation,
  primaryDimension,
  interactive = true,
  testId,
  'data-testid': dataTestId,
}: {
  points: MapPoint[];
  metric: MetricValues;
  normalization?: DataNormalization;
  transformation?: DataTransformation;
  primaryDimension?: PrimaryFilterId;
  interactive?: boolean;
  testId?: string;
  'data-testid'?: string;
}) => {
  const [isMapReady, setIsMapReady] = useState(false);

  const resolvedTestId = testId ?? dataTestId;

  return (
    <Box w="100%" h="100%" minH="0" flex="1">
      <GenericMap
        data-testid={resolvedTestId}
        points={points}
        metric={metric}
        normalization={normalization}
        transformation={transformation}
        primaryDimension={primaryDimension}
        interactive={interactive}
        isSourceReady={setIsMapReady}
      />
      {!isMapReady && <ChartLoader />}
    </Box>
  );
};

export const MapChartSelector = ({
  primaryDimension,
  metric,
  mapOptions,
}: {
  primaryDimension?: PrimaryFilterId;
  metric: MetricValues;
  mapOptions: MapPlotOptions;
}) => {
  const { revelioPageId } = useTabContentConfig();
  const effectiveFilters = useEffectiveFilters();
  const hasVisibleFilters = effectiveFilters.some((filter) => filter.isVisible);
  const isTalentDiscovery = revelioPageId === RevelioPageId.TALENT_DISCOVERY;
  const isInteractive = !(isTalentDiscovery && !hasVisibleFilters);
  const { exchangeRate } = useCurrencyPreference();

  if (isTalentDiscoveryMetric(metric)) {
    return (
      <TalentDiscoveryDataProvider exchangeRate={exchangeRate}>
        {({ data, status }) => (
          <ChartFallback status={status}>
            <MapWithLoader
              data-testid="talent-discovery-map"
              points={data?.mapData?.points ?? []}
              metric={metric}
              normalization={mapOptions?.normalization}
              transformation={mapOptions?.transformation}
              primaryDimension={primaryDimension}
              interactive={isInteractive}
            />
          </ChartFallback>
        )}
      </TalentDiscoveryDataProvider>
    );
  }
  if (metric === 'headcount') {
    return (
      <TalentDiscoveryMapDataProvider>
        {({ data, status }) => (
          <ChartFallback status={status}>
            <MapWithLoader
              data-testid="talent-discovery-map"
              points={data?.points ?? []}
              metric={metric}
              normalization={mapOptions?.normalization}
              transformation={mapOptions?.transformation}
              primaryDimension={primaryDimension}
              interactive={isInteractive}
            />
          </ChartFallback>
        )}
      </TalentDiscoveryMapDataProvider>
    );
  }
  if (isScreenerMetric(metric)) {
    if (!primaryDimension) return <ChartNotSupported />;
    return (
      <ScreenerMapDataProvider
        primaryDimension={primaryDimension}
        metric={metric}
        chartType="map"
      >
        {({ data, status }) => (
          <ChartFallback status={status}>
            <MapWithLoader
              data-testid="screener-map"
              points={data?.points ?? []}
              metric={metric}
              normalization={mapOptions?.normalization}
              transformation={mapOptions?.transformation}
            />
          </ChartFallback>
        )}
      </ScreenerMapDataProvider>
    );
  }

  if (isPostingMetric(metric)) {
    if (!primaryDimension) return <ChartNotSupported />;
    return (
      <PostingMapDataProvider
        primaryDimension={primaryDimension}
        metric={metric}
        exchangeRate={exchangeRate}
      >
        {({ data, status }) => (
          <ChartFallback status={status}>
            <MapWithLoader
              data-testid="posting-map"
              points={data?.points ?? []}
              metric={metric}
              normalization={mapOptions?.normalization}
              transformation={mapOptions?.transformation}
            />
          </ChartFallback>
        )}
      </PostingMapDataProvider>
    );
  }

  if (isTransitionMetric(metric)) {
    if (!primaryDimension) return <ChartNotSupported />;
    return (
      <TransitionMapDataProvider
        primaryDimension={primaryDimension}
        metric={metric}
      >
        {({ data, status }) => (
          <ChartFallback status={status}>
            <MapWithLoader
              data-testid="transition-map"
              points={data?.points ?? []}
              metric={metric}
              normalization={mapOptions?.normalization}
              transformation={mapOptions?.transformation}
            />
          </ChartFallback>
        )}
      </TransitionMapDataProvider>
    );
  }

  if (isSentimentMetric(metric)) {
    if (!primaryDimension) return <ChartNotSupported />;
    return (
      <SentimentMapDataProvider
        primaryDimension={primaryDimension}
        metric={metric}
      >
        {({ data, status }) => (
          <ChartFallback status={status}>
            <MapWithLoader
              data-testid="sentiment-map"
              points={data?.points ?? []}
              metric={metric}
              normalization={mapOptions?.normalization}
              transformation={mapOptions?.transformation}
            />
          </ChartFallback>
        )}
      </SentimentMapDataProvider>
    );
  }

  if (isPayMetric(metric)) {
    if (!primaryDimension) return <ChartNotSupported />;
    return (
      <CompensationMapDataProvider
        primaryDimension={primaryDimension}
        exchangeRate={exchangeRate}
      >
        {({ data, status }) => (
          <ChartFallback status={status}>
            <MapWithLoader
              data-testid="compensation-map"
              points={data?.points ?? []}
              metric={metric}
              normalization={mapOptions?.normalization}
              transformation={mapOptions?.transformation}
            />
          </ChartFallback>
        )}
      </CompensationMapDataProvider>
    );
  }

  // Default to Composition
  if (!primaryDimension) return <ChartNotSupported />;
  return (
    <CompositionMapDataProvider primaryFilterId={primaryDimension}>
      {({ data, status }) => (
        <ChartFallback status={status}>
          <MapWithLoader
            data-testid="composition-map"
            points={data?.points ?? []}
            metric={metric}
            normalization={mapOptions?.normalization}
            transformation={mapOptions?.transformation}
          />
        </ChartFallback>
      )}
    </CompositionMapDataProvider>
  );
};
